import React, { useEffect, useState } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import cx from 'classnames';

import { getFilingData } from 'api/reports';
import Spinner from 'components/spinner';
import { Report } from 'graphql-api';
import { ReportFilingDataInterface } from 'types/report';
import { HOW_TO_FILE_PATH } from 'constants/paths';
import { CONTRACTOR_REPORT_TYPE } from 'constants/common';
import FileEmployeeReport from 'components/how-to-file-page/file-employee-report';
import FileContractorReport from 'components/how-to-file-page/file-contractor-report';

function HowToFilePage(): JSX.Element {
  const {
    employeeReport,
    contractorReport
  }: { employeeReport: Report; contractorReport: Report | null } = useOutletContext();
  const [employeeFilingData, setEmployeeFilingData] = useState<ReportFilingDataInterface | null>(
    null
  );
  const [contractorFilingData, setContractorFilingData] =
    useState<ReportFilingDataInterface | null>(null);
  const [searchParams] = useSearchParams();
  const hasCompletedEmployeeReport = employeeReport?.paid === true;
  const hasCompletedContractorReport =
    contractorReport?.paid === true && contractorReport?.resultGenerated === true;
  const [isEmployeeTabSelected, setIsEmployeeTabSelected] = useState(
    searchParams.get('currentTab') !== CONTRACTOR_REPORT_TYPE && hasCompletedEmployeeReport
  );
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    void (async () => {
      const [employeeFilingData, contractorFilingData] = await Promise.all([
        hasCompletedEmployeeReport ? getFilingData(employeeReport.id) : null,
        hasCompletedContractorReport ? getFilingData(contractorReport.id) : null
      ]);
      setEmployeeFilingData(employeeFilingData);
      setContractorFilingData(contractorFilingData);
      setIsLoading(false);
    })();
  }, []);

  return (
    <>
      <div className="border-b border-gray-200 px-4 py-4 sm:px-6 lg:px-8">
        <h1 className="text-lg font-medium leading-6 text-gray-900 sm:truncate">
          How to File 2022 Report
        </h1>
      </div>
      <div className="text-base text-gray-700 mt-8 px-4 sm:px-6 lg:px-8">
        Follow the steps below to easily file your downloaded reports.
      </div>
      {isLoading ? (
        <div className="text-center pt-16">
          <Spinner className="text-gray-500" size={48} />
        </div>
      ) : (
        <div className="mt-8 px-4 sm:px-6 lg:px-8">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 text-sm font-medium whitespace-nowrap">
              {employeeFilingData !== null && (
                <a
                  onClick={() => {
                    setIsEmployeeTabSelected(true);
                    window.history.replaceState(null, '', HOW_TO_FILE_PATH);
                  }}
                  className={cx(
                    isEmployeeTabSelected
                      ? 'border-blue-500 text-blue-500'
                      : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
                    'border-b-2 pb-4 px-1 cursor-pointer'
                  )}>
                  Employee Report
                </a>
              )}
              {contractorFilingData !== null && (
                <a
                  onClick={() => {
                    setIsEmployeeTabSelected(false);
                    window.history.replaceState(
                      null,
                      '',
                      `${HOW_TO_FILE_PATH}?currentTab=${CONTRACTOR_REPORT_TYPE}`
                    );
                  }}
                  className={cx(
                    !isEmployeeTabSelected
                      ? 'border-blue-500 text-blue-500'
                      : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
                    'border-b-2 pb-4 px-1 cursor-pointer'
                  )}>
                  Labor Contractor Report
                </a>
              )}
            </nav>
          </div>

          <div className="mt-8">
            {isEmployeeTabSelected ? (
              <FileEmployeeReport employeeFilingData={employeeFilingData} />
            ) : (
              <FileContractorReport contractorFilingData={contractorFilingData} />
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default HowToFilePage;
